/** FIRUL AGENTULUI DE FAȚADĂ, în panoul asistentului din machetă.
 *
 *  Agentul lucrează minute întregi pe server; omul care se uită la machetă vede
 *  butonul asistentului pulsând, iar înăuntru, rând cu rând, ce face agentul,
 *  spus pentru el (Cătălin, 5.10.2026). Rândurile vin din
 *  `GET /api/projects/:p/solutions/:arhitectura/agent?dupa=<seq>`, sondat cât
 *  rulează jobul. Gândirea modelului nu ajunge aici: serverul n-o trimite.
 *
 *  Aici stă și partea PURĂ — ce rânduri se arată și cu ce text —, ca s-o poată
 *  proba un test fără rețea. */
import { useEffect, useRef, useState } from "react";
import * as api from "../../api";
import { esteCheie, t } from "../../i18n";
import type { MesajAgent } from "../../api";

/** Un rând gata de desenat: felul și textul în limba ecranului. */
export interface RandFir {
  seq: number;
  /** `om` = propoziția agentului pentru om (și cea de încheiere), `pas` = un
   *  pas al lui, spus scurt, `eroare` = oprirea lui, `unealta` = rândul de
   *  DEPANARE al unei unelte (fără text pentru om; `detaliu` spune ce a cerut
   *  modelul și ce a primit) — îl arată doar modul de depanare al cardului. */
  fel: "om" | "pas" | "eroare" | "unealta";
  text: string;
  /** La `unealta`: `{ unealta, arg, rezultat }`, cum le scrie serviciul
   *  (`serviciu.detaliu_unealta`); `arg`/`rezultat` pot fi un text tăiat. */
  detaliu?: Record<string, unknown>;
  /** Codul pasului (`agent.pas.stil_pus`…), pentru cine vrea să știe CE a
   *  făcut agentul, nu doar să-l citească — macheta se recitește după o
   *  scriere a lui. */
  cod?: string | null;
  /** Parametrii pasului (`n`, `varianta`…): din ei se face rezumatul rulării. */
  param?: Record<string, string | number> | null;
  /** Propoziția de ÎNCHEIERE (`final`), nu a unui tur: ea e rezultatul. */
  final?: boolean;
  /** Când a scris-o agentul (ISO). */
  la?: string | null;
}

/** MODUL DE DEPANARE al firului (Oana, 01.11.2026): sub fiecare pas, ce a
 *  cerut modelul fiecărei unelte și ce a primit (rândurile `unealta`). Nu e un
 *  buton pentru toți: îl deschide `?depanare` în adresă, o dată, și rămâne pe
 *  browser (`localStorage.ob_depanare`); `?depanare=0` îl închide. Ecranul
 *  simplu nu se schimbă pentru cine nu l-a cerut. */
export function depanareaFirului(): boolean {
  try {
    if (typeof window === "undefined") return false;
    const q = new URLSearchParams(window.location.search);
    if (q.has("depanare")) {
      if (q.get("depanare") === "0") window.localStorage.removeItem("ob_depanare");
      else window.localStorage.setItem("ob_depanare", "1");
    }
    return window.localStorage.getItem("ob_depanare") === "1";
  } catch {
    return false;
  }
}

/** Pașii după care agentul a SCRIS pe soluție: macheta are ce să recitească. */
export const PASI_CARE_SCRIU = new Set(["agent.pas.stil_pus", "agent.pas.compun", "agent.pas.compun_refuz"]);

export interface StareFir {
  /** Rularea lucrează acum (pulsul butonului, punctul din panou). */
  ruleaza: boolean;
  randuri: RandFir[];
}

/** Stările în care un job n-a terminat (ca `inlantuire.STARI_DESCHISE`). */
const DESCHISE = new Set(["pending", "running", "waiting"]);
/** Cât de des se cere firul cât lucrează agentul. */
export const SONDAJ_MS = 2500;

/** Textul unui mesaj, în limba ecranului: din codul lui când ecranul îl știe,
 *  altfel textul scris de server (românesc). `null` = nu e de arătat — un tur
 *  în care modelul n-a spus nimic omului. */
export function textulMesajului(m: MesajAgent): string | null {
  if (m.text_cod && esteCheie(m.text_cod)) return t(m.text_cod, m.text_param ?? undefined);
  const text = (m.text ?? "").trim();
  return text || null;
}

/** Rândurile de arătat, în ordine: propozițiile pentru om, pașii, oprirea. */
export function randurileFirului(mesaje: readonly MesajAgent[]): RandFir[] {
  const out: RandFir[] = [];
  for (const m of [...mesaje].sort((a, b) => a.seq - b.seq)) {
    if (m.fel === "unealta") {
      // rândul de depanare n-are text: numele uneltei îl identifică, detaliul îl desface modul de depanare
      const d = (m.detaliu ?? {}) as Record<string, unknown>;
      out.push({ seq: m.seq, fel: "unealta", text: String(d.unealta ?? ""), detaliu: d, la: m.created_at });
      continue;
    }
    const text = textulMesajului(m);
    if (!text) continue;
    const fel = m.fel === "tur" || m.fel === "final" ? "om" : m.fel === "eroare" ? "eroare" : "pas";
    out.push({ seq: m.seq, fel, text, cod: m.text_cod, param: m.text_param,
               final: m.fel === "final", la: m.created_at });
  }
  return out;
}

/** Un pas al cronologiei: propoziția agentului pentru om și, sub ea, ce a
 *  făcut (pașii-cod de după ea), spuși scurt. */
export interface PasFir {
  seq: number;
  text: string;
  /** Pașii de după propoziție, spuși scurt; un pas fără propoziție înainte
   *  (începutul rulării) stă singur, cu textul lui. */
  etichete: string[];
  eroare: boolean;
  /** Uneltele chemate sub pasul ăsta (rândurile `unealta`), pentru depanare;
   *  cele dinaintea primei propoziții intră sub primul pas. */
  unelte: RandFir[];
}

/** FIRUL CA REZUMAT ȘI CRONOLOGIE.
 *
 *  Agentul scrie o propoziție pentru om, apoi pasul-cod care o confirmă
 *  («Am îmbrăcat clădirea cu stilul ales.»). Puse rând sub rând, cele două
 *  spuneau același lucru de două ori, iar confirmarea se repeta la fiecare
 *  tur. Aici pasul-cod devine eticheta propoziției de dinaintea lui, scrisă
 *  scurt (`agent.scurt.*`); încheierea iese din cronologie și devine
 *  rezultatul, iar numerele rulării (varianta, referințele, unghiurile,
 *  observațiile) se adună într-un singur rând. */
export function rezumatulFirului(randuri: readonly RandFir[], scurt: (r: RandFir) => string) {
  const pasi: PasFir[] = [];
  let final: RandFir | null = null;
  let varianta: { varianta: number | string; din: number | string } | null = null;
  let referinte = 0, unghiuri = 0, observatii = 0;
  let inAsteptare: RandFir[] = [];                 // uneltele de dinaintea primului pas
  for (const r of randuri) {
    if (r.fel === "unealta") {
      const ultim = pasi[pasi.length - 1];
      if (ultim) ultim.unelte.push(r); else inAsteptare.push(r);
      continue;
    }
    const nume = r.cod?.startsWith("agent.pas.") ? r.cod.slice(10) : null;
    const n = Number(r.param?.n ?? 0) || 0;
    if (nume === "incep" && r.param) {
      varianta = { varianta: r.param.varianta, din: r.param.din };
      referinte = Number(r.param.referinte ?? 0) || 0;
      continue;
    }
    if (nume === "verific_randarile" || nume === "verific_schimbarea") unghiuri += n;
    if (nume === "critic" || nume === "critic_editare") observatii += n;
    if (r.fel === "om" && r.final) { final = r; continue; }
    const ultim = pasi[pasi.length - 1];
    if (r.fel === "pas" && ultim && !ultim.eroare && ultim.text) {
      const e = scurt(r);
      if (!ultim.etichete.includes(e)) ultim.etichete.push(e);
      continue;
    }
    pasi.push(r.fel === "pas"
      ? { seq: r.seq, text: "", etichete: [scurt(r)], eroare: false, unelte: inAsteptare }
      : { seq: r.seq, text: r.text, etichete: [], eroare: r.fel === "eroare", unelte: inAsteptare });
    inAsteptare = [];
  }
  return { pasi, final, varianta, referinte, unghiuri, observatii,
           la: randuri.length ? randuri[randuri.length - 1].la ?? null : null };
}

/** Mesajele noi lipite de cele știute, fără dubluri pe `seq`. */
export function lipeste(vechi: readonly MesajAgent[], noi: readonly MesajAgent[]): MesajAgent[] {
  const stiute = new Set(vechi.map((m) => m.seq));
  return [...vechi, ...noi.filter((m) => !stiute.has(m.seq))].sort((a, b) => a.seq - b.seq);
}

/** Firul ultimei rulări a agentului pe arhitectura `solutionId`.
 *
 *  `ruleazaDinRegistru` vine din registrul viu (sondat el însuși cât rulează
 *  ceva): când agentul pornește, firul începe să se ceară; cât jobul e deschis
 *  — după registru SAU după răspunsul firului —, se cere la `SONDAJ_MS`. După
 *  ce se închide, încă o cerere, pentru rândul de încheiere, și atât. */
export function useFirAgent(projectId: string, solutionId: string | null,
                            ruleazaDinRegistru: boolean): StareFir {
  const [mesaje, setMesaje] = useState<MesajAgent[]>([]);
  const [deschis, setDeschis] = useState(false);
  const job = useRef<string | null>(null);
  const ultim = useRef(0);
  const ruleaza = ruleazaDinRegistru || deschis;

  // altă arhitectură: alt fir
  useEffect(() => {
    job.current = null; ultim.current = 0;
    setMesaje([]); setDeschis(false);
  }, [projectId, solutionId]);

  useEffect(() => {
    if (!solutionId) return;
    let viu = true;
    let ceas: ReturnType<typeof setTimeout> | null = null;
    const cere = async () => {
      try {
        const r = await api.getFirAgent(projectId, solutionId, ultim.current);
        if (!viu) return;
        const id = r.job?.id ?? null;
        if (id !== job.current) {
          // O RULARE NOUĂ (regenerare, a doua variantă): firul ei pornește de
          // la zero, iar ce s-a cerut cu `dupa` al celei vechi s-ar fi pierdut
          const prima = job.current === null && ultim.current === 0;
          job.current = id; ultim.current = 0;
          setMesaje([]);
          if (!prima && id) { void cere(); return; }
        }
        if (r.mesaje.length) {
          ultim.current = Math.max(ultim.current, ...r.mesaje.map((m) => m.seq));
          setMesaje((v) => lipeste(v, r.mesaje));
        }
        setDeschis(!!r.job && DESCHISE.has(r.job.status));
      } catch {
        // o cerere picată nu golește firul: următoarea îl continuă
      }
    };
    void cere();
    if (ruleaza) {
      const bucla = () => { ceas = setTimeout(async () => { await cere(); if (viu) bucla(); }, SONDAJ_MS); };
      bucla();
    }
    return () => { viu = false; if (ceas) clearTimeout(ceas); };
  }, [projectId, solutionId, ruleaza]);

  return { ruleaza, randuri: randurileFirului(mesaje) };
}
